import React, { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled, { css } from 'styled-components';

import { CareerIcon, CareerIconName } from 'atoms';
import {
  CareerSectionCode,
  ICareerGoal,
  ICareerSection,
  ICareerSectionMetric,
} from 'core/hooks/CareerJourney/transform/transformCareerJourney';
import { skillMapHref } from 'router/careerLinks';
import { PATHS } from 'router/config';
import { careerColor } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

interface ICareerRoute {
  sections: ICareerSection[];
  goal: ICareerGoal | null;
  hasGoal: boolean;
}

type Translate = (key: string, vars?: Record<string, unknown>) => string;

const SECTION_COPY: Record<CareerSectionCode, { icon: CareerIconName; title: string }> = {
  direction: { icon: 'goal', title: 'careerCenter.plan.section.direction' },
  preparation: { icon: 'learning', title: 'careerCenter.plan.section.preparation' },
  search: { icon: 'search', title: 'careerCenter.plan.section.search' },
  recruitment: { icon: 'interview', title: 'careerCenter.plan.section.recruitment' },
};

const STATE_LABEL: Record<string, string> = {
  done: 'careerCenter.plan.state.done',
  in_progress: 'careerCenter.plan.state.in_progress',
  not_started: 'careerCenter.plan.state.not_started',
  blocked: 'careerCenter.plan.state.blocked',
};

const ITEM_COPY: Record<string, { label: string; icon: CareerIconName }> = {
  goal: { label: 'careerCenter.plan.item.goal', icon: 'briefcase' },
  skills: { label: 'careerCenter.plan.item.skills', icon: 'skills' },
  learning: { label: 'careerCenter.plan.item.learning', icon: 'learning' },
  portfolio: { label: 'careerCenter.plan.item.portfolio', icon: 'portfolio' },
  cv: { label: 'careerCenter.plan.item.cv', icon: 'cv' },
  jobs: { label: 'careerCenter.plan.item.jobs', icon: 'search' },
  applications: { label: 'careerCenter.plan.item.applications', icon: 'applications' },
  interview: { label: 'careerCenter.plan.item.interview', icon: 'interview' },
};

interface IRouteItem {
  key: string;
  value: string;
  route: string;
  isDone: boolean;
  isBlocked: boolean;
}

const count = (t: Translate, metric: ICareerSectionMetric) =>
  metric.isDone
    ? t('careerCenter.plan.state.done')
    : metric.target === null
    ? t('careerCenter.plan.item.countOpen', { value: metric.value })
    : t('careerCenter.plan.item.count', { value: metric.value, target: metric.target });

/**
 * Конкретные шаги этапа (ТЗ 4.6). Числа — с сервера; клиент добавляет только
 * навыки из прогресса цели и ссылку на ленту вакансий (у ленты числа нет).
 *
 * Завершённое пишется словом «Gotowe», а не дробью «1 z 1» / «7 z 7»
 * («визуальное упрощение», 6.5): число нужно, пока есть что досчитывать.
 */
const itemsOf = (
  t: Translate,
  section: ICareerSection,
  goal: ICareerGoal | null,
  hasGoal: boolean,
): IRouteItem[] => {
  const rows: IRouteItem[] = [];
  section.metrics.forEach((metric) => {
    if (metric.key === 'goal') {
      rows.push({
        key: 'goal',
        value: hasGoal && goal ? goal.title : t('careerCenter.plan.item.goalNone'),
        route: PATHS.careerProfessions,
        isDone: metric.isDone,
        isBlocked: false,
      });
      return;
    }
    if (metric.key === 'learning' && hasGoal && goal?.skillProgress?.requiredCount) {
      const skillsDone = goal.skillProgress.meetsCount >= goal.skillProgress.requiredCount;
      rows.push({
        key: 'skills',
        value: skillsDone
          ? t('careerCenter.plan.state.done')
          : t('careerCenter.plan.item.count', {
              value: goal.skillProgress.meetsCount,
              target: goal.skillProgress.requiredCount,
            }),
        route: skillMapHref({ track: goal.trackCode }),
        isDone: skillsDone,
        isBlocked: false,
      });
    }
    if (metric.key === 'applications' && !metric.isBlocked) {
      rows.push({
        key: 'jobs',
        value: '',
        route: PATHS.careerJobs,
        isDone: false,
        isBlocked: false,
      });
    }
    rows.push({
      key: metric.key,
      value: count(t, metric),
      route: metric.route,
      isDone: metric.isDone,
      isBlocked: metric.isBlocked,
    });
  });
  return rows;
};

/** Подпись состояния этапа — всегда словами, цвет её только сопровождает. */
const stateOf = (section: ICareerSection): { label: string; tone: StageTone } => {
  if (section.isBlocked) return { label: STATE_LABEL.blocked, tone: 'muted' };
  if (section.state === 'done') return { label: STATE_LABEL.done, tone: 'done' };
  if (section.isCurrent) return { label: 'careerCenter.plan.state.current', tone: 'current' };
  return { label: STATE_LABEL[section.state] ?? STATE_LABEL.not_started, tone: 'muted' };
};

type StageTone = 'done' | 'current' | 'muted';

/**
 * Карьерный маршрут — вертикальный accordion («визуальное упрощение», 15.09).
 *
 * Владелец в Brave: у плиток 2×2 было слишком много обозначений, шаги
 * раскрывались ОТДЕЛЬНО, под всей сеткой, а текущий этап, раскрытый этап и
 * фокус выглядели одинаково (рамка). Теперь:
 *
 * * этап — одна строка-кнопка: знак, название, состояние словами, шеврон;
 * * шаги — сразу под своим заголовком;
 * * текущий этап — метка «Teraz» на мягком жёлтом; раскрытый — только шевроном;
 *   фокус — своим контуром `:focus-visible`. Три признака больше не смешиваются.
 *
 * Раскрытие — состояние UI, не фаза студента: открыть «Poszukiwanie pracy» не
 * значит перенести туда «Teraz». По умолчанию открыт этап основного действия
 * (`isCurrent` с сервера), открыт максимум один, повторный клик сворачивает.
 * Обновление данных выбор не сбрасывает: состояние живёт, пока смонтирован
 * компонент, а `journey` при перезапросе остаётся в кеше.
 */
export const CareerRoute = memo(({ sections, goal, hasGoal }: ICareerRoute) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<CareerSectionCode | null>(
    () => sections.find((section) => section.isCurrent)?.key ?? null,
  );

  return (
    <section aria-labelledby="career-route-title">
      <Heading id="career-route-title">{t('careerCenter.plan.routeTitle')}</Heading>
      <Stages>
        {sections.map((section) => {
          const copy = SECTION_COPY[section.key];
          if (!copy) return null;
          const expanded = open === section.key;
          const state = stateOf(section);
          const panelId = `career-stage-${section.key}`;
          return (
            <li key={section.key}>
              <StageButton
                type="button"
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => setOpen(expanded ? null : section.key)}>
                <StageIcon $tone={state.tone} aria-hidden>
                  <CareerIcon
                    name={section.isBlocked ? 'lock' : state.tone === 'done' ? 'check' : copy.icon}
                    size={20}
                  />
                </StageIcon>
                <StageTitle>{t(copy.title)}</StageTitle>
                <StageState $tone={state.tone}>{t(state.label)}</StageState>
                <Chevron $open={expanded} aria-hidden>
                  <CareerIcon name="chevronDown" size={20} />
                </Chevron>
              </StageButton>
              <Panel id={panelId} role="region" aria-label={t(copy.title)} hidden={!expanded}>
                <Steps>
                  {itemsOf(t, section, goal, hasGoal).map((item) => {
                    const itemCopy = ITEM_COPY[item.key];
                    const label = t(itemCopy?.label ?? item.key);
                    return (
                      <li key={item.key}>
                        {item.isBlocked ? (
                          <LockedStep>
                            <StepIcon aria-hidden>
                              <CareerIcon name="lock" size={20} />
                            </StepIcon>
                            <StepLabel>{label}</StepLabel>
                            <StepValue>{t('careerCenter.plan.item.locked')}</StepValue>
                          </LockedStep>
                        ) : (
                          <Step to={item.route}>
                            <StepIcon aria-hidden>
                              <CareerIcon name={itemCopy?.icon ?? 'arrowRight'} size={20} />
                            </StepIcon>
                            <StepLabel>{label}</StepLabel>
                            {item.value ? (
                              <StepValue $done={item.isDone && item.key !== 'goal'}>
                                {item.value}
                              </StepValue>
                            ) : null}
                            <StepArrow aria-hidden>
                              <CareerIcon name="arrowRight" size={16} />
                            </StepArrow>
                          </Step>
                        )}
                      </li>
                    );
                  })}
                </Steps>
              </Panel>
            </li>
          );
        })}
      </Stages>
    </section>
  );
});

const Heading = styled.h2`
  margin: 0 0 16px;
  color: ${careerColor.text};
  ${careerType.h2()};
`;

const Stages = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  border-bottom: 1px solid ${careerColor.line};

  & > li {
    border-top: 1px solid ${careerColor.line};
  }
`;

const StageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 72px;
  padding: 16px 8px;
  border: 0;
  border-radius: ${careerLayout.controlRadius}px;
  background: transparent;
  color: ${careerColor.text};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${careerColor.hover};
  }
`;

const StageIcon = styled.span<{ $tone: StageTone }>`
  flex: none;
  display: inline-flex;
  color: ${({ $tone }) => ($tone === 'done' ? careerColor.done : careerColor.text2)};
`;

const StageTitle = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  ${careerType.h3()};
`;

const StageState = styled.span<{ $tone: StageTone }>`
  flex: none;
  ${careerType.meta()};

  ${({ $tone }) =>
    $tone === 'current'
      ? css`
          padding: 2px 8px;
          border-radius: ${careerLayout.controlRadius}px;
          background: ${careerColor.active};
          color: ${careerColor.text};
        `
      : $tone === 'done'
      ? css`
          color: ${careerColor.done};
        `
      : css`
          color: ${careerColor.text2};
        `}
`;

const Chevron = styled.span<{ $open: boolean }>`
  flex: none;
  display: inline-flex;
  color: ${careerColor.text2};
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const Panel = styled.div`
  padding: 0 0 16px 40px;
`;

const Steps = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
`;

const stepRow = css`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: ${careerLayout.primaryButton}px;
  padding: 8px;
  border-radius: ${careerLayout.controlRadius}px;
  color: ${careerColor.text};
  ${careerType.body()};
`;

const Step = styled(Link)`
  ${stepRow};
  text-decoration: none;

  &:hover {
    color: ${careerColor.text};
    background: ${careerColor.hover};
  }
`;

const LockedStep = styled.div`
  ${stepRow};
  color: ${careerColor.text2};
`;

const StepIcon = styled.span`
  flex: none;
  display: inline-flex;
  color: ${careerColor.text2};
`;

const StepLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const StepValue = styled.span<{ $done?: boolean }>`
  flex: 0 1 auto;
  min-width: 0;
  text-align: right;
  overflow-wrap: anywhere;
  color: ${({ $done }) => ($done ? careerColor.done : careerColor.text2)};
  ${careerType.meta()};
`;

const StepArrow = styled.span`
  flex: none;
  display: inline-flex;
  color: ${careerColor.text2};
`;
